import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Dog, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PetForm } from './PetSetup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const getPhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return path.startsWith('/api')
    ? `${process.env.REACT_APP_BACKEND_URL}${path}`
    : `${process.env.REACT_APP_BACKEND_URL}/api${path}`;
};


const PET_SIZE_LABEL = { pequeno: 'Pequeño', mediano: 'Mediano', grande: 'Grande' };

const ClientPets = () => {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPetForm, setShowPetForm] = useState(false);
  const [editingPet, setEditingPet] = useState(null);

  const loadPets = async () => {
    try {
      const res = await api.get('/pets').catch(() => ({ data: [] }));
      setPets(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPets();
  }, []);

  const openCreate = () => {
    setEditingPet(null);
    setShowPetForm(true);
  };

  const openEdit = (pet) => {
    setEditingPet(pet);
    setShowPetForm(true);
  };

  const deletePet = async (petId) => {
    if (!window.confirm('¿Eliminar esta mascota?')) return;

    try {
      await api.delete(`/pets/${petId}`);
      toast.success('Mascota eliminada');
      loadPets();
    } catch {
      toast.error('No se pudo eliminar la mascota');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-pets-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#E6202E]">
            <ArrowLeft className="w-4 h-4" />
            Volver al panel
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <p className="text-sm font-semibold text-[#E6202E] mb-2">Mis mascotas</p>
              <h1 className="text-3xl md:text-4xl font-black text-gray-900">Mascotas registradas</h1>
              <p className="text-gray-500 mt-2">
                Agrega, edita y revisa las mascotas que usarás para solicitar servicios.
              </p>
            </div>

            <Button onClick={openCreate} className="bg-[#E6202E] hover:bg-[#D31522] rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Agregar mascota
            </Button>
          </div>
        </div>

        <Dialog
          open={showPetForm}
          onOpenChange={(open) => {
            setShowPetForm(open);
            if (!open) setEditingPet(null);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingPet ? 'Editar mascota' : 'Agregar mascota'}</DialogTitle>
            </DialogHeader>

            <PetForm
              pet={editingPet}
              onSaved={() => {
                loadPets();
                setEditingPet(null);
                setShowPetForm(false);
              }}
              onCancel={() => {
                setEditingPet(null);
                setShowPetForm(false);
              }}
            />
          </DialogContent>
        </Dialog>

        {pets.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-sm border p-10 text-center">
            <Dog className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900">No tienes mascotas registradas</h2>
            <p className="text-gray-500 mt-2 mb-5">Agrega tu primera mascota para crear solicitudes.</p>
            <Button onClick={openCreate} className="bg-[#E6202E] hover:bg-[#D31522] rounded-xl">
              Agregar mascota
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pets.map(pet => (
              <div
                key={pet.pet_id}
                className="bg-white rounded-3xl shadow-sm border p-5 hover:border-[#E6202E]/40 transition-colors"
              >
                <div className="flex gap-5">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-red-50 flex items-center justify-center flex-shrink-0">
                    {pet.photo ? (
                      <img
                        src={getPhotoUrl(pet.photo)}
                        className="w-full h-full object-cover"
                        alt={pet.name}
                      />
                    ) : (
                      <Dog className="w-8 h-8 text-[#E6202E]" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="font-black text-xl text-gray-900">{pet.name}</h2>

                    <p className="text-gray-500 mt-1">
                      {pet.breed || 'Sin raza'}
                    </p>

                    <p className="text-sm text-gray-400 mt-1">
                      {pet.sex || 'Sin sexo'} • {pet.age || '-'} años • {PET_SIZE_LABEL[pet.size] || pet.size || 'Sin tamaño'}
                    </p>

                    <div className="flex gap-2 mt-5">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEdit(pet)}
                        className="rounded-xl"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Editar
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deletePet(pet.pet_id)}
                        className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPets;
